import { LeagueMember, MemberStats, Power } from "../types/game";

export interface MatchTeamInput {
  name: string;
  members: LeagueMember[];
  goalkeeperLevel: number;
  activePower?: Power | null;
}

export interface MatchAction {
  sec: number;
  minute: number;
  type: "goal" | "opp_goal" | "shot" | "tackle" | "card" | "normal";
  text: string;
  actorName: string;
  assisterName?: string;
}

export interface PlayerRating {
  name: string;
  power: number;
  isMe: boolean;
  isBot: boolean;
  rating: string;
  goals: number;
  assists: number;
  comment: string;
}

export interface DebugEntry {
  seq: number;
  minute: number;
  isHome: boolean;
  isCounter: boolean;
  // Joueurs impliqués dans le duel
  attackerName?: string;
  defenderName?: string;
  // Duel 1 : dribble individuel vs tacle individuel (après sort mults)
  attDrib: number;
  defTackle: number;
  dribP: number;
  dribSuccess: boolean;
  // Duel 2 : frappe vs bloc (si tir tenté)
  shotTaken?: boolean;
  attFinish?: number;
  defBlock?: number;
  onTargetP?: number;
  onTarget?: boolean;
  // Duel 3 : gardien d'équipe vs frappe (si tir cadré)
  saveP?: number;
  saved?: boolean;
  outcome: AttOutcome;
}

/** Bundles collectifs (agrégés équipe) exposés pour le debug. */
export interface TeamBundles {
  control: number;       // possession — somme d'équipe
  keeper: number;        // gardien — somme d'équipe + bonus gk
  dribMult: number;      // multiplicateur sort sur dribble
  finishMult: number;    // multiplicateur sort sur frappe
  tackleMult: number;    // multiplicateur sort sur tacle
  counterTrigger: number;
  counterMult: number;
}

/** Bundles individuels d'un joueur — calculés à partir de ses 4 stats d'action. */
export interface PlayerBundles {
  drib: number;    // Technique + 0.5·Vitesse
  finish: number;  // Technique + 0.6·Physique
  tackle: number;  // Défense + 0.3·Physique
  block: number;   // 0.7·Défense + 0.35·Physique
}

/** Calcule les 4 bundles individuels d'un joueur. */
export function playerBundles(member: LeagueMember): PlayerBundles {
  const s = member.stats ?? profileFromPower(member.power);
  return {
    drib:   s.technique + 0.5 * s.vitesse,
    finish: s.technique + 0.6 * s.physique,
    tackle: s.defense   + 0.3 * s.physique,
    block:  s.defense   * 0.7 + 0.35 * s.physique,
  };
}

export interface MatchSimResult {
  homeScore: number;
  awayScore: number;
  result: "win" | "draw" | "loss";
  points: number;
  timeline: MatchAction[];
  ratings: PlayerRating[];
  stats: MatchStatsSheet;
  mvp: string;
  teamPower: number;
  oppPower: number;
  opponentName: string;
  debugLog: DebugEntry[];
  homeBundles: TeamBundles;   // bundles collectifs domicile (après sort)
  awayBundles: TeamBundles;   // bundles collectifs adverse (après sort)
  homeMembers: LeagueMember[];
  awayMembers: LeagueMember[];
}

/* ─── Formule de résolution des duels (Courbe de puissance) ──────────────────
   P = clamp(CAP_MAX − MARGE × (Stat_Def / Stat_Att)^EXP, MIN, MAX)
   · À stats égales (ratio = 1) : 0.85 − 0.35 = 0.50 → duel 50/50.
   · L'exposant accélère la progression quand les stats sont proches,
     valorisant chaque point de statistique individuel.
   · Plafond strict [15 %, 85 %] : toujours une chance de renversement. */
export const DUEL_CAP_MAX  = 0.85;
export const DUEL_MARGE    = 0.35;
export const DUEL_EXPONENT = 1.65;
export const DUEL_MIN      = 0.15;
export const DUEL_MAX      = 0.85;

/** Clamp spécifique au gardien — plafond plus serré pour garder des scores réalistes. */
export const SAVE_MIN = 0.42;
export const SAVE_MAX = 0.91;

export function duelProbability(stat: number, opponentStat: number): number {
  const s = Math.max(0, stat);
  const o = Math.max(0, opponentStat);
  if (s <= 0) return DUEL_MIN;
  const ratio = o / s;
  const raw = DUEL_CAP_MAX - DUEL_MARGE * Math.pow(ratio, DUEL_EXPONENT);
  return Math.min(DUEL_MAX, Math.max(DUEL_MIN, raw));
}

/* ─── Statistiques agrégées d'un match (Feuille de match) ────────────────────
   Dérivées de façon déterministe (seed) à partir du rapport attaque/défense via
   la formule soft-cap. Reproduisent les indicateurs cités dans le game design :
   défenses réussies, tirs, passes réussies, interceptions, dribbles…           */
export interface MatchTeamStats {
  possession: number; // %
  shots: number;
  shotsOnTarget: number;
  saves: number; // défenses réussies (arrêts décisifs)
  passes: number;
  passAccuracy: number; // %
  interceptions: number;
  dribbles: number;
  tackles: number;
  counters: number; // contre-attaques déclenchées
}

export interface MatchStatsSheet {
  home: MatchTeamStats;
  away: MatchTeamStats;
}

/* ─── Force offensive / défensive ────────────────────────────────────────── */
export interface Strength {
  attack: number;
  defense: number;
  gkBonus: number;
}

/** Profil 4-stats par défaut quand un membre n'en a pas (bots alliés, legacy) :
 *  répartition équilibrée de sa puissance. */
export function profileFromPower(power: number): MemberStats {
  const q = power / 4;
  return { technique: q, defense: q, physique: q, vitesse: q };
}

/** Somme des 4 stats d'action de toute l'équipe. */
export function teamProfile(members: LeagueMember[]): MemberStats {
  return members.reduce<MemberStats>(
    (acc, m) => {
      const s = m.stats ?? profileFromPower(m.power);
      acc.technique += s.technique;
      acc.defense += s.defense;
      acc.physique += s.physique;
      acc.vitesse += s.vitesse;
      return acc;
    },
    { technique: 0, defense: 0, physique: 0, vitesse: 0 }
  );
}

/** Force offensive / défensive dérivée du profil 4-stats + niveau gardien.
 *  Attaque = Technique + 0.6·Physique + 0.5·Vitesse (percer, frappe, profondeur).
 *  Défense = Défense + 0.4·Physique + 0.3·Vitesse + gardien (interception,
 *  duel à l'épaule, rattraper un sprint). */
export function profileToStrength(p: MemberStats, gkLevel: number): Strength {
  const gkBonus = gkLevel * 20;
  return {
    attack: p.technique + 0.6 * p.physique + 0.5 * p.vitesse,
    defense: p.defense + 0.4 * p.physique + 0.3 * p.vitesse + gkBonus,
    gkBonus,
  };
}

/** Force agrégée d'une équipe à partir de la puissance brute (profil équilibré).
 *  Utilisé par le helper UI computeMatchStats qui ne connaît que les puissances. */
function strengthFromPower(power: number, gkLevel: number): Strength {
  return profileToStrength(profileFromPower(power), gkLevel);
}

/** RNG déterministe (LCG) — mêmes entrées ⇒ même feuille de match. */
function seededRng(seed: number): () => number {
  let s = (Math.abs(Math.trunc(seed)) % 2147483647) + 1;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

type SideForce = { attack: number; defense: number };

/** Indicateurs d'une équipe dérivés des forces via la formule soft-cap. */
function buildTeamStats(
  me: SideForce,
  opp: SideForce,
  poss: number,
  rng: () => number
): MatchTeamStats {
  const jitter = (base: number, spread: number) =>
    Math.round(base + (rng() - 0.5) * 2 * spread);

  // Probabilité moyenne de percer la défense adverse (soft-cap).
  const breakThrough = duelProbability(me.attack, opp.defense);
  const shots = Math.max(3, jitter(7 + breakThrough * 14, 2));
  const shotsOnTarget = Math.max(
    1,
    Math.min(shots, Math.round(shots * (0.35 + breakThrough * 0.35)))
  );
  // Défenses réussies = arrêts du gardien face aux tirs cadrés adverses.
  const keeperStop = duelProbability(me.defense, opp.attack);
  const oppBreak = duelProbability(opp.attack, me.defense);
  const oppShots = Math.max(3, Math.round(7 + oppBreak * 14));
  const oppOnTarget = Math.max(1, Math.round(oppShots * (0.35 + oppBreak * 0.35)));
  const saves = Math.max(0, Math.round(oppOnTarget * (0.45 + keeperStop * 0.4)));

  const passes = Math.max(120, jitter(260 + poss * 4.5, 25));
  const passAccuracy = Math.min(
    93,
    Math.max(64, Math.round(66 + breakThrough * 24 + (rng() - 0.5) * 6))
  );
  const interceptions = Math.max(2, jitter(6 + (1 - oppBreak) * 14, 2));
  const dribbles = Math.max(1, jitter(4 + breakThrough * 12, 2));
  const tackles = Math.max(3, jitter(9 + duelProbability(me.defense, opp.attack) * 14, 3));
  const counters = Math.max(0, jitter(duelProbability(me.defense, opp.attack) * 4, 1));

  return {
    possession: poss,
    shots,
    shotsOnTarget,
    saves,
    passes,
    passAccuracy,
    interceptions,
    dribbles,
    tackles,
    counters,
  };
}

/** Feuille de match complète à partir des forces des deux équipes. */
function statsSheetFromStrengths(
  H: SideForce,
  A: SideForce,
  seed: number
): MatchStatsSheet {
  const rng = seededRng(seed * 7919 + 17);
  const homePoss = Math.round(
    Math.min(72, Math.max(28, duelProbability(H.attack, A.attack) * 100 + (rng() - 0.5) * 8))
  );
  return {
    home: buildTeamStats(H, A, homePoss, rng),
    away: buildTeamStats(A, H, 100 - homePoss, rng),
  };
}

/** Helper UI : feuille de match à partir des seules puissances (profil équilibré).
 *  Le moteur (simulateMatch) produit lui une feuille à partir des vrais profils. */
export function computeMatchStats(
  homePower: number,
  awayPower: number,
  homeGkLevel = 1,
  awayGkLevel = 1,
  seed = 1
): MatchStatsSheet {
  return statsSheetFromStrengths(
    strengthFromPower(homePower, homeGkLevel),
    strengthFromPower(awayPower, awayGkLevel),
    seed
  );
}

function pickByPower(members: LeagueMember[], seed: number): LeagueMember {
  if (!members.length)
    return { name: "Un joueur", power: 30, isMe: false, isBot: false };
  const total = members.reduce((s, m) => s + m.power, 0);
  let r = (((seed * 9301 + 49297) % 233280) / 233280) * total;
  for (const m of members) {
    r -= m.power;
    if (r <= 0) return m;
  }
  return members[0];
}

/* ─── Moteur de duels minute par minute ─────────────────────────────────────
   Le score ÉMERGE de micro-événements résolus par la formule soft-cap. Chaque
   séquence oppose les stats CONTEXTUELLES de l'attaque et de la défense :
   dribble (Technique+½Vitesse) vs tacle (Défense+0.3Physique) → frappe
   (Technique+0.6Physique) vs bloc → tir vs Gardien Bot (le goulot qui garde des
   scores réalistes). Constantes calibrées par simulation Monte-Carlo. */

/* ─── Contexte d'équipe : collectif + multiplicateurs de sort ────────────────
   control / keeper restent des sommes d'équipe.
   dribMult / finishMult / tackleMult sont les gains/malus des Sorts Actifs.
   Les bundles individuels (drib, finish, tackle, block) sont calculés à la
   volée via playerBundles(member) au moment du duel, puis multipliés. */
interface TeamCtx {
  members: LeagueMember[];
  control: number;       // possession — agrégat d'équipe
  keeper: number;        // gardien — agrégat d'équipe + bonus gkLevel
  dribMult: number;      // sort precision / build_up
  finishMult: number;    // sort precision
  tackleMult: number;    // sort tackle_safety
  counterTrigger: number;
  counterMult: number;
}

function teamCtx(members: LeagueMember[], gkLevel: number): TeamCtx {
  const c = teamProfile(members);
  const gkBonus = gkLevel * 20;
  return {
    members,
    control: c.technique + c.vitesse + 0.5 * c.physique,
    keeper:  gkBonus * 4 + c.defense * 0.55,
    dribMult: 1, finishMult: 1, tackleMult: 1,
    counterTrigger: 0.28,
    counterMult: 1.5,
  };
}

/** Effets des Sorts (Orbes) sur les multiplicateurs du contexte d'équipe. */
function applyPowerToCtx(
  power: Power | null | undefined,
  self: TeamCtx,
  opp: TeamCtx
): void {
  if (!power) return;
  const v = power.value;
  switch (power.effectType) {
    case "precision":       self.finishMult *= 1 + v; self.dribMult *= 1 + v * 0.5; break;
    case "tackle_safety":   self.tackleMult *= 1 + v * 0.5; break;
    case "goalkeeper_buff": self.keeper *= 1 + v; break;
    case "counter_chance":  self.counterTrigger = Math.min(0.6, self.counterTrigger * (1 + v)); self.counterMult += v * 0.6; break;
    case "build_up":        self.dribMult *= 1 + v * 0.5; break;
    case "enemy_debuff":    opp.dribMult *= 1 - v; opp.finishMult *= 1 - v; break;
  }
}

/** Pioche un membre pondéré par puissance à partir d'une valeur rng [0,1). */
function pickByRng(members: LeagueMember[], r: number): LeagueMember {
  if (!members.length) return { name: "Inconnu", power: 30, isMe: false, isBot: false };
  const total = members.reduce((s, m) => s + m.power, 0) || 1;
  let v = r * total;
  for (const m of members) { v -= m.power; if (v <= 0) return m; }
  return members[members.length - 1];
}

interface RawStats {
  passes: number;
  dribbles: number;
  shots: number;
  onTarget: number;
  goals: number;
  tackles: number;
  interceptions: number;
  saves: number;
  counters: number;
  lostBalls: number;
  attacks: number;
}
const emptyRaw = (): RawStats => ({
  passes: 0, dribbles: 0, shots: 0, onTarget: 0, goals: 0,
  tackles: 0, interceptions: 0, saves: 0, counters: 0, lostBalls: 0, attacks: 0,
});

type AttOutcome =
  | "goal" | "shot_saved" | "shot_off" | "tackle" | "interception" | "counter" | "buildup";

interface DebugCollector {
  attackerName: string; defenderName: string;
  attDrib: number; defTackle: number;
  dribP: number; dribSuccess: boolean;
  shotTaken: boolean;
  attFinish?: number; defBlock?: number; onTargetP?: number; onTarget?: boolean;
  saveP?: number; saved?: boolean;
}

/** Résout une attaque en opposant un joueur actif à un défenseur (duel 1v1).
 *  Les bundles sont individuels ; seuls le gardien et la possession restent
 *  collectifs. Les multiplicateurs de sort modifient les bundles individuels. */
function resolveAttack(
  att: TeamCtx,
  def: TeamCtx,
  sa: RawStats,
  sd: RawStats,
  rng: () => number,
  isCounter: boolean,
  dc?: DebugCollector
): AttOutcome {
  sa.attacks++;
  sa.passes += 3 + Math.floor(rng() * 7);

  // Pioche un attaquant et un défenseur individuels (pondérés par puissance).
  const attPlayer = pickByRng(att.members, rng());
  const defPlayer = pickByRng(def.members, rng());
  const ab = playerBundles(attPlayer);
  const db = playerBundles(defPlayer);

  const effAttDrib   = ab.drib   * att.dribMult;
  const effDefTackle = db.tackle * def.tackleMult;
  const effAttFinish = ab.finish * att.finishMult;
  const effDefBlock  = db.block;

  if (dc) {
    dc.attackerName = attPlayer.name; dc.defenderName = defPlayer.name;
    dc.attDrib = effAttDrib; dc.defTackle = effDefTackle;
    dc.shotTaken = false;
  }

  // 1. Percer la ligne défensive : dribble individuel vs tacle individuel.
  let dribP = duelProbability(effAttDrib, effDefTackle);
  if (isCounter) dribP = Math.min(0.9, dribP * att.counterMult);
  if (dc) dc.dribP = dribP;
  if (rng() > dribP) {
    if (dc) dc.dribSuccess = false;
    sa.lostBalls++;
    if (rng() < 0.55) { sd.tackles++; return "tackle"; }
    sd.interceptions++;
    if (rng() < def.counterTrigger) return "counter";
    return "interception";
  }
  if (dc) dc.dribSuccess = true;
  sa.dribbles++;

  // 2. L'action débouche-t-elle sur un tir ?
  if (rng() > 0.78) return "buildup";
  sa.shots++;
  if (dc) dc.shotTaken = true;

  // 3. Tir cadré ? Frappe individuelle vs bloc individuel.
  const onTargetP = duelProbability(effAttFinish, effDefBlock);
  if (dc) { dc.attFinish = effAttFinish; dc.defBlock = effDefBlock; dc.onTargetP = onTargetP; }
  if (rng() > onTargetP) {
    if (dc) dc.onTarget = false;
    return "shot_off";
  }
  sa.onTarget++;
  if (dc) dc.onTarget = true;

  // 4. Gardien collectif (goulot d'étranglement) vs frappe individuelle.
  // Clamp [SAVE_MIN, SAVE_MAX] distinct du clamp général — maintient des scores réalistes.
  const saveP = Math.min(SAVE_MAX, Math.max(SAVE_MIN, duelProbability(def.keeper, effAttFinish)));
  if (dc) dc.saveP = saveP;
  if (rng() < saveP) {
    if (dc) dc.saved = true;
    sd.saves++;
    return "shot_saved";
  }
  if (dc) dc.saved = false;
  sa.goals++;
  return "goal";
}

function finalizeStats(s: RawStats, possession: number): MatchTeamStats {
  const passAccuracy = Math.min(95, Math.max(60, Math.round((100 * s.passes) / (s.passes + s.lostBalls || 1))));
  return {
    possession,
    shots: s.shots,
    shotsOnTarget: s.onTarget,
    saves: s.saves,
    passes: s.passes,
    passAccuracy,
    interceptions: s.interceptions,
    dribbles: s.dribbles,
    tackles: s.tackles,
    counters: s.counters,
  };
}

/** Nombre de séquences d'attaque simulées sur 90 minutes (calibré). */
const MATCH_SEQUENCES = 98;

export function simulateMatch(
  home: MatchTeamInput,
  away: MatchTeamInput,
  seed?: number
): MatchSimResult {
  const homePower = home.members.reduce((s, m) => s + m.power, 0);
  const awayPower = away.members.reduce((s, m) => s + m.power, 0);

  // Contextes d'équipe (profils 4-stats agrégés) + effets des sorts actifs.
  const homeCtx = teamCtx(home.members, home.goalkeeperLevel);
  const awayCtx = teamCtx(away.members, away.goalkeeperLevel);
  applyPowerToCtx(home.activePower, homeCtx, awayCtx);
  applyPowerToCtx(away.activePower, awayCtx, homeCtx);

  const matchSeed = seed ?? Math.floor(Math.random() * 9_999_991);
  const rng = seededRng(matchSeed * 2654435 + 13);
  const share = homeCtx.control / (homeCtx.control + awayCtx.control || 1);

  const sH = emptyRaw();
  const sA = emptyRaw();
  const timeline: MatchAction[] = [];
  const debugLog: DebugEntry[] = [];

  const ctxToBundle = (ctx: TeamCtx): TeamBundles => ({
    control: ctx.control, keeper: ctx.keeper,
    dribMult: ctx.dribMult, finishMult: ctx.finishMult, tackleMult: ctx.tackleMult,
    counterTrigger: ctx.counterTrigger, counterMult: ctx.counterMult,
  });

  // Émet l'événement marquant d'une attaque (perspective domicile pour les
  // ratings : buts des deux camps, tirs cadrés & stops défensifs du domicile).
  const emit = (
    attIsHome: boolean,
    att: TeamCtx,
    def: TeamCtx,
    outcome: AttOutcome,
    minute: number,
    salt: number
  ) => {
    const pick = (m: LeagueMember[], s: number) => pickByPower(m, matchSeed + minute + salt + s);
    if (outcome === "goal") {
      const scorer = pick(att.members, 1);
      const rest = att.members.filter((m) => m.name !== scorer.name);
      const assister = pick(rest.length ? rest : att.members, 2);
      if (attIsHome) {
        timeline.push({
          sec: minute, minute, type: "goal",
          text: `🚨 ${minute}' | BUT ! ${scorer.name}, sur une passe de ${assister.name} ! 🎯`,
          actorName: scorer.name, assisterName: assister.name,
        });
      } else {
        timeline.push({
          sec: minute, minute, type: "opp_goal",
          text: `❌ ${minute}' | But de ${scorer.name} pour ${away.name}.`,
          actorName: scorer.name,
        });
      }
    } else if (outcome === "shot_saved" && attIsHome) {
      const shooter = pick(att.members, 3);
      timeline.push({
        sec: minute, minute, type: "shot",
        text: `🔥 ${minute}' | Frappe cadrée de ${shooter.name}, arrêt du gardien ! 🧤`,
        actorName: shooter.name,
      });
    } else if ((outcome === "tackle" || outcome === "interception") && !attIsHome) {
      // L'attaque adverse a échoué : c'est un stop défensif du domicile (def).
      const stopper = pick(def.members, 4);
      const verb = outcome === "tackle" ? "Tacle décisif" : "Interception";
      timeline.push({
        sec: minute, minute, type: "tackle",
        text: `🛡️ ${minute}' | ${verb} de ${stopper.name} !`,
        actorName: stopper.name,
      });
    }
  };

  let seqIdx = 0;
  for (let k = 0; k < MATCH_SEQUENCES; k++) {
    const minute = Math.min(90, Math.max(1, Math.floor((k / MATCH_SEQUENCES) * 90) + 1));
    const homeAtt = rng() < share;
    const att = homeAtt ? homeCtx : awayCtx;
    const def = homeAtt ? awayCtx : homeCtx;
    const sa = homeAtt ? sH : sA;
    const sd = homeAtt ? sA : sH;

    const dc: DebugCollector = { attackerName: "", defenderName: "", attDrib: 0, defTackle: 0, dribP: 0, dribSuccess: false, shotTaken: false };
    const res = resolveAttack(att, def, sa, sd, rng, false, dc);
    emit(homeAtt, att, def, res, minute, 0);
    debugLog.push({ seq: ++seqIdx, minute, isHome: homeAtt, isCounter: false,
      attackerName: dc.attackerName, defenderName: dc.defenderName,
      attDrib: dc.attDrib, defTackle: dc.defTackle, dribP: dc.dribP, dribSuccess: dc.dribSuccess,
      shotTaken: dc.shotTaken, attFinish: dc.attFinish, defBlock: dc.defBlock,
      onTargetP: dc.onTargetP, onTarget: dc.onTarget,
      saveP: dc.saveP, saved: dc.saved, outcome: res });

    if (res === "counter") {
      sd.counters++;
      const dc2: DebugCollector = { attackerName: "", defenderName: "", attDrib: 0, defTackle: 0, dribP: 0, dribSuccess: false, shotTaken: false };
      const cres = resolveAttack(def, att, sd, sa, rng, true, dc2);
      emit(!homeAtt, def, att, cres, minute, 50);
      debugLog.push({ seq: ++seqIdx, minute, isHome: !homeAtt, isCounter: true,
        attackerName: dc2.attackerName, defenderName: dc2.defenderName,
        attDrib: dc2.attDrib, defTackle: dc2.defTackle, dribP: dc2.dribP, dribSuccess: dc2.dribSuccess,
        shotTaken: dc2.shotTaken, attFinish: dc2.attFinish, defBlock: dc2.defBlock,
        onTargetP: dc2.onTargetP, onTarget: dc2.onTarget,
        saveP: dc2.saveP, saved: dc2.saved, outcome: cres });
    }
  }

  const homeScore = sH.goals;
  const awayScore = sA.goals;
  const result: "win" | "draw" | "loss" =
    homeScore > awayScore ? "win" : homeScore === awayScore ? "draw" : "loss";
  const points = result === "win" ? 3 : result === "draw" ? 1 : 0;

  const homePoss = Math.round((100 * sH.attacks) / (sH.attacks + sA.attacks || 1));
  const stats: MatchStatsSheet = {
    home: finalizeStats(sH, homePoss),
    away: finalizeStats(sA, 100 - homePoss),
  };

  // Player ratings (équipe domicile) dérivés des actions de la timeline.
  const ratings: PlayerRating[] = home.members
    .map((member) => {
      let base = 5.0 + (member.power - 30) / 30;
      let goals = 0, assists = 0, shots = 0;
      for (const act of timeline) {
        if (act.actorName === member.name) {
          if (act.type === "goal") goals++;
          if (act.type === "shot") shots++;
        }
        if (act.assisterName === member.name && act.type === "goal") assists++;
      }
      base += goals * 2.5 + assists * 1.5 + shots * 0.4;
      if (result === "win") base += 0.5;
      if (result === "loss") base -= 0.5;
      const rating = Math.max(3, Math.min(10, base));

      let comment = "Match solide.";
      if (goals > 0) comment = "Homme du match ! Clinique devant le but.";
      else if (assists > 0) comment = "Excellent passeur, très altruiste.";
      else if (member.power > 45) comment = "Patron du milieu, a dicté le tempo.";

      return {
        name: member.name,
        power: member.power,
        isMe: member.isMe,
        isBot: member.isBot,
        rating: rating.toFixed(1),
        goals,
        assists,
        comment,
      };
    })
    .sort((a, b) => Number(b.rating) - Number(a.rating));

  return {
    homeScore,
    awayScore,
    result,
    points,
    timeline,
    ratings,
    stats,
    mvp: ratings[0]?.name ?? "Inconnu",
    teamPower: homePower,
    oppPower: awayPower,
    opponentName: away.name,
    debugLog,
    homeBundles: ctxToBundle(homeCtx),
    awayBundles: ctxToBundle(awayCtx),
    homeMembers: home.members,
    awayMembers: away.members,
  };
}
